import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ProjectPhotosPanel from "@/components/ProjectPhotosPanel";
import ProjectDocumentsPanel from "@/components/ProjectDocumentsPanel";
import { PHOTO_ACCEPT, uploadPhotosToProject } from "@/utils/photoUploads";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const CLICK_DELAY_MS = 260;

/**
 * Two Supervisor-only cells rendered inline in the Master Board row:
 *   1. Photos: single-tap → gallery dialog. Double-tap → upload picker
 *      (uses the same tenant-scoped POST /photos endpoint as the panel).
 *   2. Docs: single-tap → documents dialog (read-only for Supervisor).
 *
 * A 260ms click resolver guarantees single- and double-tap are mutually
 * exclusive — a double-tap NEVER opens the gallery first. Works on desktop
 * dblclick and iOS/Android double-tap.
 */
export default function SupervisorAttachmentsCell({ projectId, testIdPrefix = "master-supervisor" }) {
  const [photosCount, setPhotosCount] = useState(0);
  const [documentsCount, setDocumentsCount] = useState(0);
  const [openPanel, setOpenPanel] = useState(null); // "photos" | "documents" | null
  const [uploading, setUploading] = useState(false);
  const clickTimer = useRef(null);
  const fileInputRef = useRef(null);

  const loadCounts = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/photos/count`),
        axios.get(`${API}/projects/${projectId}/documents/count`),
      ]);
      setPhotosCount(p.data?.count || 0);
      setDocumentsCount(d.data?.count || 0);
    } catch {
      // Silent — cell just shows zeros.
    }
  }, [projectId]);

  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => () => {
    if (clickTimer.current) clearTimeout(clickTimer.current);
  }, []);

  const handlePhotosClick = () => {
    if (uploading) return;
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      // second click within window → double-tap: open file picker
      fileInputRef.current?.click();
      return;
    }
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      setOpenPanel("photos");
    }, CLICK_DELAY_MS);
  };

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      await uploadPhotosToProject(projectId, files);
      toast.success(`Uploaded ${files.length} photo${files.length > 1 ? "s" : ""}.`);
      await loadCounts();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Fragment>
      <td data-testid={`${testIdPrefix}-photos-cell-${projectId}`}>
        <input
          accept={PHOTO_ACCEPT}
          data-testid={`${testIdPrefix}-photos-input-${projectId}`}
          multiple
          onChange={handleFilesSelected}
          ref={fileInputRef}
          style={{ display: "none" }}
          type="file"
        />
        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
          <button
            className="table-edit-button"
            data-testid={`${testIdPrefix}-photos-${projectId}`}
            disabled={uploading}
            onClick={handlePhotosClick}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", minWidth: 62 }}
            type="button"
          >
            {uploading ? (
              <Loader2 size={13} className="animate-spin" data-testid={`${testIdPrefix}-photos-uploading-${projectId}`} />
            ) : (
              <Camera size={13} />
            )}
            <span>{uploading ? "Uploading…" : photosCount}</span>
          </button>
          <small style={{ color: "#94a3b8", fontSize: "0.65rem", lineHeight: 1 }}>
            Double-tap to upload
          </small>
        </div>
      </td>
      <td data-testid={`${testIdPrefix}-docs-cell-${projectId}`}>
        <button
          className="table-edit-button"
          data-testid={`${testIdPrefix}-docs-${projectId}`}
          onClick={() => setOpenPanel("documents")}
          style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
          type="button"
        >
          <FileText size={13} /> {documentsCount}
        </button>
      </td>

      <Dialog
        open={openPanel !== null}
        onOpenChange={(v) => { if (!v) { setOpenPanel(null); loadCounts(); } }}
      >
        <DialogContent data-testid={`${testIdPrefix}-attach-dialog`}>
          <DialogHeader>
            <DialogTitle>{openPanel === "photos" ? "Project photos" : "Project documents"}</DialogTitle>
          </DialogHeader>
          {openPanel === "photos" && (
            <ProjectPhotosPanel defaultOpen projectId={projectId} />
          )}
          {openPanel === "documents" && (
            <ProjectDocumentsPanel defaultOpen projectId={projectId} />
          )}
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}
